import React from "react";
import { Link } from "react-router";

/**
 * Doelsoft Accessibility Statement Component
 * Single-file React component (default export) using TailwindCSS for styling.
 * - Semantic landmarks
 * - Skip link
 * - Keyboard-focusable elements with visible focus outline
 * - Uses accessible HTML (headings, lists, links, form example)
 *
 * You can drop this component into any React app that has TailwindCSS configured.
 */

export default function DoelsoftAccessibilityStatement() {
  return (
    <div className="min-h-screen mt-16 bg-gray-50 text-gray-900 antialiased">
      {/* Skip link for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:bg-white focus:px-3 focus:py-2 focus:rounded-md focus:shadow-md"
      >
        Skip to main content
      </a>

      <header className="bg-white shadow-sm">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <h1 className="text-2xl font-semibold">Doelsoft Website Accessibility Statement</h1>
          <p className="mt-1 text-sm text-gray-600">Committed to inclusive digital experiences</p>
        </div>
      </header>

      <main id="main-content" className="max-w-5xl mx-auto px-6 py-8">
        <article className="prose prose-lg max-w-none">
          <section aria-labelledby="introduction">
            <h2 id="introduction">Introduction</h2>
            <p>
              Doelsoft is committed to ensuring digital accessibility for people with disabilities. We are
              continually improving the user experience for everyone and applying the relevant accessibility
              standards. This statement reflects our goal of adhering to the Web Content Accessibility Guidelines
              (WCAG) 2.2 standards and striving to enhance accessibility.
            </p>
          </section>

          <section aria-labelledby="standards" className="mt-6">
            <h2 id="standards">Accessibility Standards</h2>
            <p>The following principles from WCAG 2.2 guide our accessibility efforts:</p>
            <ul>
              <li><strong>Perceivable:</strong> Information and user interface components must be presentable to users in ways they can perceive.</li>
              <li><strong>Operable:</strong> User interface components and navigation must be operable.</li>
              <li><strong>Understandable:</strong> Information and the operation of the user interface must be understandable.</li>
              <li><strong>Robust:</strong> Content must be robust enough to be interpreted reliably by a wide variety of user agents, including assistive technologies.</li>
            </ul>
          </section>

          <section aria-labelledby="statement-components" className="mt-6">
            <h2 id="statement-components">Statement Components</h2>

            <h3 id="alt-text">1. Alternative Text for Images</h3>
            <p>
              All images on the Doelsoft website should have descriptive alternative text (alt text) that conveys the
              meaning and context of the image. Decorative images should use empty alt attributes (alt="") so that
              screen readers ignore them.
            </p>

            <h3 id="multimedia">2. Accessible Multimedia Players</h3>
            <p>
              Multimedia players used on the website should be fully accessible and support keyboard navigation. Controls
              must be clearly labeled and easily identifiable. Videos should generally avoid autoplay so users can control playback.
            </p>

            <h3 id="scrolling">3. Accessible Scrolling & Navigation</h3>
            <p>
              The website will ensure that scrolling functionality is accessible. Content requiring scrolling must be usable
              with both mouse and keyboard. Skip navigation links are provided to bypass repetitive content.
            </p>

            <h3 id="contrast-resize">4. Color Contrast and Text Resizing</h3>
            <p>
              Text and images of text should have a contrast ratio of at least 4.5:1, except for large text (which should
              have a contrast ratio of at least 3:1) and incidental text or images. Users should be able to resize text up to 200%
              without losing content or functionality.
            </p>

            <h3 id="keyboard">5. Keyboard Accessibility</h3>
            <p>
              All functionalities of the website should be accessible via keyboard. Focus indicators will be visible to ensure
              users can easily see where they are on the page.
            </p>

            <h3 id="navigation">6. Clear Navigation and Structure</h3>
            <p>
              The website should have a consistent navigation structure. Headings and labels must be clear and descriptive,
              and a logical document order should be maintained so users can predict and understand the flow of the website.
            </p>

            <h3 id="forms">7. Form Accessibility</h3>
            <p>
              Form fields should be clearly labeled. Instructions and error messages must be provided to guide users through form
              completion. Forms should support both keyboard and screen reader navigation.
            </p>
          </section>

          <section aria-labelledby="improvement" className="mt-6">
            <h2 id="improvement">Continuous Improvement</h2>
            <p>Doelsoft is committed to ongoing accessibility improvements. We will:</p>
            <ul>
              <li>Conduct regular accessibility audits.</li>
              <li>Provide training to our web development and content creation teams.</li>
              <li>Encourage feedback from users to identify and address accessibility issues.</li>
            </ul>
          </section>

          <section aria-labelledby="feedback" className="mt-6">
            <h2 id="feedback">Feedback and Contact Information</h2>
            <p>
              We welcome feedback on the accessibility of the Doelsoft website. Please let us know if you encounter any barriers
              or have suggestions for improvement.
            </p>
            <p>
              <Link
            to="/contact"
                className="inline-block underline focus:outline-none focus:ring-2 focus:ring-offset-2 rounded"
                aria-label="Contact Doelsoft about accessibility"
              >
                Contact Doelsoft
              </Link>
            </p>
          </section>

          <section aria-labelledby="closing" className="mt-6">
            <h2 id="closing">Closing</h2>
            <p>
              Doelsoft is dedicated to making our website accessible to everyone and will continue to work towards full compliance
              with WCAG 2.2 standards. Thank you for your support and cooperation.
            </p>
          </section>

          <hr className="my-8" />

          <footer className="text-sm text-gray-600">
            <p>
              By aiming to adhere to these guidelines, Doelsoft ensures that our website is accessible and usable by the widest
              possible audience, including persons with disabilities.
            </p>
          </footer>
        </article>
      </main>

      {/* Visually hidden styles utility (for sr-only) in case Tailwind's plugin isn't enabled */}
      <style>{`
        .sr-only { 
          position: absolute; 
          width: 1px; 
          height: 1px; 
          padding: 0; 
          margin: -1px; 
          overflow: hidden; 
          clip: rect(0, 0, 0, 0); 
          white-space: nowrap; 
          border: 0; 
        }
      `}</style>
    </div>
  );
}
